<template>
  <div>
    <b-form-group
      label="2: Define totals"
      label-size="lg"
      label-cols-md="3"
      :disabled="disabled"
    >
      <DefineUnitForTotal
        ref="define-unit-for-total"
        :total-unit-type.sync="totalUnitType"
        :total-unit-label.sync="totalUnitLabel"
        :is-multiple-units.sync="isMultipleUnits"
      />

      <b-form-group
        label="Select the fields that sum to the bubble total"
        label-cols="3"
        data-cy="bubble-total-fields-radio-input"
      >
        <template v-for="field in fieldMap">
          <TotalItem
            v-if="field.type === MONETARY || field.type === NUMERIC"
            :key="`define-field-total-${field.column}`"
            :item="field"
            :state="state"
            @toggle-total="updateFieldMapTypeUnit"
          />
        </template>
      </b-form-group>

      <b-alert
        :show="v.$dirty && v.$error"
        variant="warning"
      >
        Please select at least one field as a total to give the bubbles size
      </b-alert>
    </b-form-group>
  </div>
</template>

<script lang="ts">
import Vue from 'vue'
import Component from 'vue-class-component'

import TotalItem from '@/components/admin/upload/CSVSettings/DefineTotals/TotalItem.vue'
import DefineUnitForTotal from '@/components/admin/upload/CSVSettings/DefineTotals/DefineUnitForTotal.vue'
import { FieldType, FieldTypes } from '@/types'
import { useUploadStore } from '@/store/admin/upload'

const components = {
  TotalItem,
  DefineUnitForTotal,
}
const props = {
  v: {
    type: Object,
    required: true,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
}

// eslint-disable-next-line no-use-before-define
@Component<DefineTotals>({
  components,
  watch: {
    totalUnitTypeLabel () {
      this.updateFieldMapTypeUnit()
    },
    fieldMap (newMap, oldMap) {
      if (oldMap.length === 0) {
        this.setInitialTotalUnit()
      }
    },
  },
})
export default class DefineTotals extends Vue.extend({ props }) {
  upload = useUploadStore()
  totalUnitType: Exclude<FieldType, typeof FieldTypes.CALCULATION> = FieldTypes.NUMERIC
  totalUnitLabel: string = ''
  MONETARY = FieldTypes.MONETARY
  NUMERIC = FieldTypes.NUMERIC
  isMultipleUnits = false

  get fieldMap () {
    return this.upload.fieldMap
  }

  setInitialTotalUnit () {
    // set initial totalUnit based on FieldMap when updating existing version
    const totalFields = this.fieldMap.filter(({ total }) => total)
    if (totalFields.length > 0) {
      // At least one item has been set as total so must be an existing version
      const uniqueTypes = new Set(totalFields.map(({ type }) => type))
      if (uniqueTypes.size === 1 && uniqueTypes.has(FieldTypes.NUMERIC)) {
        const uniqueUnits = new Set(totalFields.map(({ unit }) => unit))
        if (uniqueUnits.size === 1) {
          this.totalUnitType = FieldTypes.NUMERIC
          this.totalUnitLabel = Array.from(uniqueUnits)[0]
        }
      } else if (uniqueTypes.size === 1 && uniqueTypes.has(FieldTypes.MONETARY)) {
        this.totalUnitType = FieldTypes.MONETARY
      } else {
        this.isMultipleUnits = true
      }
    }
  }

  get state () {
    return this.v.$dirty ? !this.v.$error : null
  }

  get totalUnitTypeLabel () {
    // Used for watch triggering only
    return [this.totalUnitType, this.totalUnitLabel]
  }

  updateFieldMapTypeUnit () {
    this.fieldMap.forEach(field => {
      if (field.total) {
        field.type = this.totalUnitType as Exclude<FieldType, typeof FieldTypes.CALCULATION>
        field.unit = this.totalUnitLabel
      } else if (field.type !== FieldTypes.STRING) {
        field.type = FieldTypes.NUMERIC
        field.unit = ''
      }
    })
  }
}
</script>
